/* Shared wizard form primitives — kept minimal and dense. */
import { useState } from "react";

export function Field({ label, hint, required, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {label} {required && <span className="text-red-600">*</span>}
            </span>
            {children}
            {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
        </label>
    );
}

export function Section({ title, description, children }) {
    return (
        <section className="mb-6">
            <div className="mb-3">
                <div className="text-[13px] font-semibold tracking-tight">{title}</div>
                {description && <div className="mt-0.5 text-[11px] text-muted-foreground">{description}</div>}
            </div>
            <div className="akm-card p-4 space-y-3">{children}</div>
        </section>
    );
}

export function Toggle({ label, description, checked, onChange, testId }) {
    return (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background px-3 py-2.5">
            <div className="min-w-0">
                <div className="text-[13px] font-medium">{label}</div>
                {description && <div className="text-[11px] text-muted-foreground">{description}</div>}
            </div>
            <button
                type="button"
                onClick={() => onChange(!checked)}
                data-testid={testId}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}
            >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-1"}`} />
            </button>
        </div>
    );
}

export function TagInput({ value = [], onChange, placeholder = "Add and press Enter", restrictTo = null, normalize = null }) {
    const [input, setInput] = useState("");
    const add = () => {
        let v = input.trim();
        if (normalize) v = normalize(v);
        if (!v) { setInput(""); return; }
        if (restrictTo && !restrictTo.includes(v)) { setInput(""); return; }
        if (!value.includes(v)) onChange([...value, v]);
        setInput("");
    };
    return (
        <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background p-1.5">
            {value.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[11px]">
                    {t}
                    <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="text-muted-foreground hover:text-foreground">×</button>
                </span>
            ))}
            <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                onBlur={add}
                placeholder={placeholder}
                className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                list={restrictTo ? "wizard-tag-options" : undefined}
            />
            {restrictTo && (
                <datalist id="wizard-tag-options">
                    {restrictTo.map((v) => <option key={v} value={v} />)}
                </datalist>
            )}
        </div>
    );
}

// ISO 639-1 codes commonly used in the platform's UI/booking engine.
export const LANGUAGE_CODES = [
    "sq", "en", "it", "de", "fr", "es", "el", "sr", "hr", "sl", "mk",
    "bg", "ro", "tr", "ru", "uk", "pl", "cs", "sk", "hu", "nl", "pt",
    "sv", "no", "da", "fi", "zh", "ja", "ar", "he",
];

export function CheckboxPill({ checked, onChange, children, testId }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            data-testid={testId}
            className={`rounded-md border px-2.5 py-1.5 text-[12px] font-medium capitalize transition-colors ${
                checked ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card text-muted-foreground hover:bg-muted/60"
            }`}
        >
            {children}
        </button>
    );
}
